<template>
  <LoadingComponent v-if="loading" />
  <template v-else-if="!loading && localDashboard">
    <div class="shrink-0">
      <DashboardHeader :dashboard="localDashboard">
        <template #cta>
          <BaseButton
            v-if="$can('manage_dossier')"
            icon="plus"
            :title="$t($t('button.add_new_f', { var: $t('attributes.task') }))"
            @click="getNewTask"
          />
          <DashboardPanelControl
            v-model:dashboard="localDashboard"
            v-model:require-save-dashboard="requireSaveDashboard"
            :need-save-locked="loading"
            :count-filter="validFilter.length ?? 0"
            @dashboard-changed="dashboardChanged"
          >
            <template #display-options-content="{ close }">
              <DisplayOptionsTaskContent
                :sorting="localDashboard.sort"
                @update:sorting="updateSorting"
                @close-popover="() => closePopover(close)"
              />
            </template>
            <template #filter-content="{ close }">
              <FilterLinesComponent
                v-model:filters="localDashboard.filters"
                v-model:require-save-dashboard="requireSaveDashboard"
                :filters-available="availableFilter"
                @close-popover="() => closePopover(close)"
              />
            </template>
          </DashboardPanelControl>
        </template>
      </DashboardHeader>
    </div>
    <div
      class="h-full overflow-y-scroll"
      :class="[
        groupByMode === 'status'
          ? 'flex flex-row gap-x-4'
          : 'flex flex-col gap-2'
      ]"
    >
      <template v-if="groupByMode === 'status'">
        <template v-for="taskGroup in taskGroupBy()" :key="taskGroup.id">
          <div
            v-if="!taskGroup.tasks"
            class="mt-20 text-center text-2xl font-bold text-slate-300"
          >
            {{ $t('global.no_task_assign') }}
          </div>
          <div class="flex min-w-0 flex-1 flex-col gap-2">
            <div
              v-if="taskGroup.name"
              :data-tint="taskGroup.color"
              :class="{
                'border-primary text-primary bg-primary-50 dark:bg-primary/900 rounded-t border-b-4 text-center':
                  statusGroupMode
              }"
              class="relative flex h-8 flex-row items-center justify-center"
            >
              <h1 class="capitalize-first shrink-0 text-base font-semibold">
                {{ taskGroup.name }}
              </h1>
              <span
                class="bg-primary text-contrast-primary squircle-lg absolute top-1 right-1 aspect-square h-5 rounded-lg px-1.5"
              >
                {{ taskGroup.tasks.length }}
              </span>
            </div>
            <div
              :data-tint="taskGroup.color"
              class="squircle-lg flex items-center justify-center rounded-lg border-2 border-dashed py-3 text-xl font-bold uppercase"
              @dragenter="
                (event) => event.target.classList.add('bg-primary/50')
              "
              @dragleave="
                (event) => event.target.classList.remove('bg-primary/50')
              "
              @dragover.prevent
              @drop="onDrop($event, taskGroup)"
            >
              {{ $t('global.drop') }}
            </div>
            <div class="flex flex-1 flex-col gap-2 overflow-y-scroll">
              <template v-if="taskGroup.tasks">
                <div v-for="task in taskGroup.tasks" :key="task.id">
                  <TaskDossierCard
                    :task="task"
                    :is-item-task="true"
                    :refresh-task-dossier="$route.name === 'tasks_index_route'"
                    :draggable="groupByMode === 'status'"
                    :status-click-cycle="statusClickCycle"
                    @is-dragging="(dragging) => (isDragging = dragging)"
                    @refresh-list="fetchTasks"
                    @task-status-updated="handleTaskStatusUpdated"
                  />
                </div>
              </template>
            </div>
          </div>
        </template>
      </template>
      <div v-else-if="groupByMode === 'categories'" class="flex flex-col gap-4">
        <div
          v-if="taskCategoriesWithTasks.length > 0"
          class="flex flex-wrap gap-2 border-b pb-2"
        >
          <button
            v-for="cat in taskCategoriesWithTasks"
            :key="cat.id"
            class="flex items-center gap-2 rounded-md border px-3 py-1 text-sm transition"
            :class="[
              selectedCategoryId === cat.id
                ? 'bg-primary border-primary text-white'
                : 'bg-gray-100 hover:bg-gray-200'
            ]"
            @click="selectedCategoryId = cat.id"
          >
            <span
              class="flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold"
              :class="
                selectedCategoryId === cat.id
                  ? 'text-primary bg-white'
                  : 'bg-gray-300 text-gray-700'
              "
            >
              {{ cat.count }}
            </span>
            <span>{{ cat.name }}</span>
          </button>
        </div>
        <div class="grid grid-cols-3 gap-2">
          <TaskDossierCard
            v-for="task in tasksBySelectedCategory"
            :key="task.id"
            :task="task"
            :is-item-task="true"
            :status-click-cycle="statusClickCycle"
            @refresh-list="fetchTasks"
            @task-status-updated="handleTaskStatusUpdated"
          />
        </div>
      </div>
    </div>
    <TaskDossierModal
      v-model:open="showEditTaskModal"
      v-model:edition-mode="modalEditionMode"
      :refresh-task-dossier="true"
      @refresh-list="fetchTasks"
    />
  </template>
</template>

<script>
import LoadingComponent from '@c/TheLoadingComponent.vue'
import DashboardPanelControl from '@c/Dashboard/Components/DashboardPanelControl.vue'
import FilterLinesComponent from '@c/Dashboard/Components/FilterLinesComponent.vue'
import DashboardHeader from '@c/Dashboard/Components/DashboardHeader.vue'
import { mapActions, mapGetters } from 'vuex'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import WalkmanButton from '@c/WalkmanButton.vue'
import TaskDossierCard from '@/views/dossier/form/subpages/taskDossier/components/taskDossierList/components/TaskDossierCard.vue'
import DisplayOptionsTaskContent from '@c/Dashboard/Content/Task/panel/DisplayOptionsTaskContent.vue'
import TaskDossierModal from '@ui/modal/taskDossier/TaskDossierModal.vue'

export default {
  name: 'SupplierOrderContent',
  components: {
    TaskDossierModal,
    DisplayOptionsTaskContent,
    TaskDossierCard,
    WalkmanButton,
    BaseButton,
    LoadingComponent,
    DashboardPanelControl,
    FilterLinesComponent,
    DashboardHeader
  },
  props: {
    currentBoard: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      localDashboard: null,
      loading: false,
      isSupplierOrderModalOpen: false,
      tasks: [],
      metadata: [],
      requireSaveDashboard: false,
      selectedCategoryId: null,
      taskSortAsc: true,
      isDragging: false,
      showEditTaskModal: false,
      modalEditionMode: false
    }
  },
  computed: {
    ...mapGetters({
      task_categories: 'metadata/getMetadataTaskCategories',
      task_statuses: 'metadata/getMetadataTaskStatuses',
      task_dossier: 'taskDossier/getTaskDossier',
      availableFilter: 'metadata/getAvailableFilterTaskDossier',
      dataTransferObject: 'taskDossier/getDataTransferObject'
    }),
    validFilter() {
      return (
        this.localDashboard.filters?.filter(
          (filter) =>
            filter.column &&
            filter.values != null &&
            (Array.isArray(filter.values) ? filter.values.length > 0 : true) &&
            filter.condition
        ) ?? []
      )
    },
    groupByMode() {
      return this.localDashboard?.sort?.group || 'categories'
    },
    statusGroupMode() {
      return this.groupByMode === 'status'
    },
    statusClickCycle() {
      return (
        (this.localDashboard?.sort?.statusInteraction ?? 'cycle') === 'cycle'
      )
    },
    filteredTasks() {
      return this.tasks
    },
    taskCategoriesWithTasks() {
      if (!this.filteredTasks?.length) return []
      const map = new Map()
      this.filteredTasks.forEach((task) => {
        map.set(task.category_id, (map.get(task.category_id) || 0) + 1)
      })
      return (this.task_categories ?? [])
        .filter((cat) => map.has(cat.id))
        .map((cat) => ({ ...cat, count: map.get(cat.id) }))
    },
    tasksBySelectedCategory() {
      const tasks =
        this.selectedCategoryId === null
          ? this.filteredTasks
          : this.filteredTasks.filter(
              (t) => t.category_id === this.selectedCategoryId
            )
      const dir = this.localDashboard?.sort?.direction
      if (!dir) return tasks
      return [...tasks].sort((a, b) => {
        const nameA = a.name?.[this.$i18n.locale] ?? ''
        const nameB = b.name?.[this.$i18n.locale] ?? ''
        return dir === 'asc'
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA)
      })
    },
    myTasksFlat() {
      const myId = this.$store.getters['auth/getUser']?.id
      return this.filteredTasks.filter(
        (t) => t.executors?.includes(myId) || t.responsible_id === myId
      )
    }
  },
  watch: {
    currentBoard: {
      handler(newValue, oldValue) {
        if (newValue?.id !== oldValue?.id) {
          this.fetchTaskDashboardConfig()
        }
      },
      deep: true
    },
    showEditTaskModal(newValue) {
      if (newValue === false && this.dossier) {
        // this.processFetchDossierByIDAction(this.dossier.id)
        this.$emit('refresh-list')
      }
    }
  },
  mounted() {
    this.fetchTaskDashboardConfig()
  },
  methods: {
    ...mapActions({
      processFetchTemplateDashboardByIdAction:
        'dashboard/fetchTemplateDashboardById',
      processFetchAllTasksAction: 'taskDossier/fetchAllDossierTasks',
      processUpdateDossierTaskStatusAction: 'dossier/updateDossierTaskStatus'
    }),
    getNewTask() {
      this.showEditTaskModal = true
    },
    updateSorting(newSorting) {
      this.localDashboard.sort = newSorting
    },
    fetchTasks: function () {
      let params = {
        filters: this.validFilter
      }

      this.processFetchAllTasksAction(params).then((response) => {
        this.tasks = response.task_dossiers
        // this.metadata = response.metadata
      })
    },
    fetchTaskDashboardConfig() {
      const targetId = this.currentBoard.id
      this.loading = true
      this.tasks = []
      this.localDashboard = null
      this.processFetchTemplateDashboardByIdAction({
        id: targetId
      }).then((response) => {
        if (this.currentBoard.id !== targetId) return
        this.loading = false
        this.localDashboard = response.template_dashboard

        if (!this.localDashboard.sort) {
          this.localDashboard.sort = {
            group: 'categories',
            direction: null,
            visibleStatuses: [0, 1, 2],
            statusInteraction: 'cycle'
          }
        } else {
          if (!this.localDashboard.sort.visibleStatuses) {
            this.localDashboard.sort.visibleStatuses = []
          }
          if (!this.localDashboard.sort.statusInteraction) {
            this.localDashboard.sort.statusInteraction = 'cycle'
          }
        }

        this.fetchTasks()
      })
    },
    taskGroupBy() {
      const mode = this.groupByMode
      if (mode === 'status') {
        return this.taskGroupByStatus()
      }
      return this.taskGroupByTaskCategory()
    },
    taskGroupByStatus() {
      if (!this.task_statuses) return []
      const visibleStatuses = this.localDashboard?.sort?.visibleStatuses ?? []
      const statusList =
        this.isDragging || visibleStatuses.length === 0
          ? this.task_statuses
          : this.task_statuses.filter((s) => visibleStatuses.includes(s.id))
      return statusList.map((status) => ({
        id: status.id,
        name: this.$t('enums.task.status.' + status.id),
        color: status.bg_color,
        tasks: this.sortByUpdatedAt(
          this.filteredTasks.filter((t) => t.status === status.id)
        )
      }))
    },
    taskGroupByTaskCategory() {
      if (!this.task_categories) return []

      const categories_ids = this.task_categories.map((cat) => cat.id)

      const noCategoryGroup = {
        id: 0,
        name: this.$t('global.no_category'),
        color: '#e2e8f0',
        tasks: this.filteredTasks.filter(
          (t) => !categories_ids.includes(t.category_id)
        )
      }

      const categoryGroups = this.task_categories.map((cat) => ({
        id: cat.id,
        name: cat.name,
        color: cat.color || '#cbd5e1',
        tasks: this.filteredTasks.filter((t) => t.category_id === cat.id)
      }))

      const allGroups =
        noCategoryGroup.tasks.length > 0
          ? [noCategoryGroup, ...categoryGroups]
          : categoryGroups

      return allGroups.filter((group) => group.tasks.length > 0)
    },
    sortByUpdatedAt(tasks) {
      return [...tasks].sort((a, b) => {
        const dateA = a.updated_at ? new Date(a.updated_at).getTime() : 0
        const dateB = b.updated_at ? new Date(b.updated_at).getTime() : 0
        return dateB - dateA
      })
    },
    handleTaskStatusUpdated({ taskId, newStatus, task }) {
      const target = this.tasks.find((t) => t.id === taskId)
      if (target) {
        if (task) {
          Object.assign(target, task)
        } else {
          target.status = newStatus
          target.updated_at = new Date().toISOString()
        }
      }
    },
    closePopover(close) {
      if (typeof close === 'function') {
        close()
      }
      this.fetchTasks()
    },
    onDrop(event, taskGroup) {
      const draggableTask = this.dataTransferObject?.task
      if (!draggableTask) return
      const target = this.tasks.find((t) => t.id === draggableTask.id)
      if (target) {
        target.status = taskGroup.id
        target.updated_at = new Date().toISOString()
      }
      event.preventDefault()
      event.currentTarget.classList.remove('bg-primary/50')
      this.isDragging = false
      this.processUpdateDossierTaskStatusAction({
        id: draggableTask.id,
        new_status: taskGroup.id
      }).then(() => {
        this.$h.toastSuccess(
          this.$t('toasts.updated_f', { var: this.$t('attributes.task') })
        )
      })
    },
    dashboardChanged() {
      this.fetchTaskDashboardConfig()
    }
  }
}
</script>
